/**
 * Customer App CMS — Super Admin console
 * Path: /admin/customer-app-cms
 *
 * Lets admins:
 *   • Toggle page visibility in the customer app
 *   • Reorder pages via up/down buttons
 *   • Drill into a page → toggle & reorder its features
 *   • Reset to factory defaults
 *
 * Backed by `/api/crm/cms/customer-app` (GET, PUT, /reset).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import {
  Eye, EyeSlash, ArrowUp, ArrowDown, ArrowsClockwise,
  Layout, Sparkle, FloppyDisk, CaretRight, LockKey, ArrowLeft,
} from "@phosphor-icons/react";

const move = (arr, from, to) => {
  if (to < 0 || to >= arr.length) return arr;
  const c = [...arr];
  const [x] = c.splice(from, 1);
  c.splice(to, 0, x);
  return c.map((r, i) => ({ ...r, order: i }));
};

export default function CustomerAppCms() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusPage, setFocusPage] = useState(null); // page key when drilled-in

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/cms/customer-app");
      setCfg(r.data);
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.put("/crm/cms/customer-app", {
        pages: cfg.pages,
        features: cfg.features,
        unavailable_overlays: cfg.unavailable_overlays || {},
      });
      toast.success("Customer app configuration saved");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Restore factory defaults? Any manual toggles will be lost.")) return;
    setSaving(true);
    try {
      const r = await api.post("/crm/cms/customer-app/reset");
      setCfg(r.data);
      setDirty(false);
      toast.success("Restored defaults");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const updatePages = (fn) => setCfg(c => {
    const next = { ...c, pages: fn(c.pages) };
    setDirty(true);
    return next;
  });
  const updateFeatures = (pageKey, fn) => setCfg(c => {
    const feats = c.features[pageKey] || [];
    const next = { ...c, features: { ...c.features, [pageKey]: fn(feats) } };
    setDirty(true);
    return next;
  });

  // Overlay: "This feature is currently not available in your location"
  const OVERLAY_PAGES = ["health", "hygiene", "care"];
  const updateOverlay = (pageKey, patch) => setCfg(c => {
    const cur = (c.unavailable_overlays || {})[pageKey] || {};
    const next = {
      ...c,
      unavailable_overlays: {
        ...(c.unavailable_overlays || {}),
        [pageKey]: { ...cur, ...patch },
      },
    };
    setDirty(true);
    return next;
  });

  const visibleCount = useMemo(() => {
    if (!cfg) return { pages: 0, features: 0 };
    const pages = cfg.pages.filter(p => p.visible).length;
    const features = Object.values(cfg.features || {}).reduce(
      (n, arr) => n + arr.filter(f => f.visible).length, 0);
    return { pages, features };
  }, [cfg]);

  return (
    <AppShell
      title="Customer App · Content Management"
      subtitle="Toggle page & feature visibility · Reorder menus"
      actions={
        <div className="flex gap-2">
          <button onClick={load} disabled={loading}
            data-testid="cms-refresh"
            className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2] disabled:opacity-40">
            <ArrowsClockwise size={12} weight="bold"/> Reload
          </button>
          <button onClick={reset} disabled={saving}
            data-testid="cms-reset"
            className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2] disabled:opacity-40">
            <Sparkle size={12} weight="bold"/> Reset defaults
          </button>
          <button onClick={save} disabled={!dirty || saving}
            data-testid="cms-save"
            className="inline-flex items-center gap-2 h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            <FloppyDisk size={12} weight="bold"/> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="cms-customer-app">
        {loading || !cfg ? (
          <div className="py-14 text-center text-sm text-[#8A8780]">Loading configuration…</div>
        ) : (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={Layout} label="Pages" value={cfg.pages.length} sub={`${visibleCount.pages} visible`} tone="slate"/>
              <KpiCard icon={Eye} label="Features" value={Object.values(cfg.features).reduce((n, arr) => n + arr.length, 0)} sub={`${visibleCount.features} visible`} tone="red"/>
              <KpiCard icon={ArrowsClockwise} label="Last saved" value={cfg.updated_at ? new Date(cfg.updated_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"} sub={cfg.updated_by_name || ""} tone="green"/>
              <KpiCard icon={LockKey} label="Protected" value={cfg.pages.filter(p => p.protected).length} sub="Cannot be hidden" tone="amber"/>
            </div>

            {focusPage ? (
              <>
                {OVERLAY_PAGES.includes(focusPage) && (
                  <UnavailableOverlayEditor
                    pageKey={focusPage}
                    value={(cfg.unavailable_overlays || {})[focusPage] || {}}
                    onChange={(patch) => updateOverlay(focusPage, patch)}
                  />
                )}
                <FeaturePanel
                  pageKey={focusPage}
                  pageLabel={cfg.pages.find(p => p.key === focusPage)?.label || focusPage}
                  features={cfg.features[focusPage] || []}
                  onBack={() => setFocusPage(null)}
                  onToggle={(idx) => updateFeatures(focusPage, feats => feats.map((f, i) => i === idx ? { ...f, visible: !f.visible } : f))}
                  onUp={(idx) => updateFeatures(focusPage, feats => move(feats, idx, idx - 1))}
                  onDown={(idx) => updateFeatures(focusPage, feats => move(feats, idx, idx + 1))}
                />
              </>
            ) : (
              <PagePanel
                pages={cfg.pages}
                featureCounts={Object.fromEntries(Object.entries(cfg.features).map(([k, arr]) => [k, arr.length]))}
                onDrill={setFocusPage}
                onToggle={(idx) => updatePages(pages => pages.map((p, i) => i === idx ? { ...p, visible: !p.visible } : p))}
                onUp={(idx) => updatePages(pages => move(pages, idx, idx - 1))}
                onDown={(idx) => updatePages(pages => move(pages, idx, idx + 1))}
              />
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

// ─── Page-level panel ─────────────────────────────────────────
const PagePanel = ({ pages, featureCounts, onDrill, onToggle, onUp, onDown }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
    <div className="px-4 py-3 border-b border-[#E5E2D9] bg-[#FAFAF7] flex items-center gap-2">
      <Layout size={14} weight="bold" className="text-[#DC2626]"/>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">
        Pages ({pages.length})
      </div>
      <div className="ml-auto text-[11px] text-[#8A8780] italic">
        Order sets display sequence on the customer app · protected pages can&apos;t be hidden
      </div>
    </div>
    <ul className="divide-y divide-[#F0EEE7]">
      {pages.map((p, i) => (
        <li key={p.key}
            className={`px-4 py-3 flex items-center gap-3 ${p.visible ? "" : "opacity-60"}`}
            data-testid={`cms-page-${p.key}`}>
          <div className="w-8 h-8 rounded-md bg-[#F7F6F2] font-mono text-xs font-bold text-[#5C5A55] flex items-center justify-center shrink-0">
            {i + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[#111827] flex items-center gap-2">
              {p.label || p.key}
              {p.protected && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#FFF8E6] text-[#7A5A11] font-bold">
                  <LockKey size={9} weight="bold"/> protected
                </span>
              )}
            </div>
            <div className="text-[11px] text-[#8A8780] font-mono">/{p.key}</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] font-bold mr-2">
            {featureCounts[p.key] || 0} feats
          </div>
          <IconBtn icon={ArrowUp} onClick={() => onUp(i)} disabled={i === 0} testid={`cms-page-up-${p.key}`}/>
          <IconBtn icon={ArrowDown} onClick={() => onDown(i)} disabled={i === pages.length - 1} testid={`cms-page-down-${p.key}`}/>
          <ToggleBtn checked={p.visible} disabled={p.protected} onClick={() => onToggle(i)} testid={`cms-page-toggle-${p.key}`}/>
          <button
            onClick={() => onDrill(p.key)}
            data-testid={`cms-page-drill-${p.key}`}
            className="h-8 px-3 border border-[#E5E2D9] rounded-md text-xs font-semibold hover:bg-[#F7F6F2] inline-flex items-center gap-1"
          >
            Features <CaretRight size={11} weight="bold"/>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

// ─── Feature-level panel ──────────────────────────────────────
const FeaturePanel = ({ pageKey, pageLabel, features, onBack, onToggle, onUp, onDown }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
    <div className="px-4 py-3 border-b border-[#E5E2D9] bg-[#FAFAF7] flex items-center gap-2">
      <button onClick={onBack} className="h-8 px-2 rounded-md text-xs hover:bg-[#F7F6F2] inline-flex items-center gap-1" data-testid="cms-feat-back">
        <ArrowLeft size={12} weight="bold"/> Pages
      </button>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">
        {pageLabel} · Features ({features.length})
      </div>
      <div className="ml-auto text-[11px] text-[#8A8780] font-mono">/{pageKey}</div>
    </div>
    {features.length === 0 ? (
      <div className="py-14 text-center text-sm text-[#8A8780]">
        No configurable features on this page.
      </div>
    ) : (
      <ul className="divide-y divide-[#F0EEE7]">
        {features.map((f, i) => (
          <li key={f.key}
              className={`px-4 py-3 flex items-center gap-3 ${f.visible ? "" : "opacity-60"}`}
              data-testid={`cms-feat-${pageKey}-${f.key}`}>
            <div className="w-8 h-8 rounded-md bg-[#F7F6F2] font-mono text-xs font-bold text-[#5C5A55] flex items-center justify-center shrink-0">
              {i + 1}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#111827] flex items-center gap-2">
                {f.label || f.key}
                {f.protected && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#FFF8E6] text-[#7A5A11] font-bold">
                    <LockKey size={9} weight="bold"/> protected
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[#8A8780] font-mono">{f.key}</div>
            </div>
            <IconBtn icon={ArrowUp} onClick={() => onUp(i)} disabled={i === 0} testid={`cms-feat-up-${f.key}`}/>
            <IconBtn icon={ArrowDown} onClick={() => onDown(i)} disabled={i === features.length - 1} testid={`cms-feat-down-${f.key}`}/>
            <ToggleBtn checked={f.visible} disabled={f.protected} onClick={() => onToggle(i)} testid={`cms-feat-toggle-${f.key}`}/>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const IconBtn = ({ icon: Icon, onClick, disabled, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="w-8 h-8 rounded-md border border-[#E5E2D9] hover:bg-[#F7F6F2] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center"
  >
    <Icon size={12} weight="bold" className="text-[#5C5A55]"/>
  </button>
);

const ToggleBtn = ({ checked, disabled, onClick, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1 border transition-colors ${
      checked
        ? "bg-[#EEF7EC] text-[#166534] border-[#C6D4C2] hover:bg-[#DCF0D8]"
        : "bg-[#F4C7B9] text-[#7A2E18] border-[#F4C7B9] hover:bg-[#F1B2A0]"
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {checked ? <Eye size={12} weight="fill"/> : <EyeSlash size={12} weight="fill"/>}
    {checked ? "Visible" : "Hidden"}
  </button>
);

const KpiCard = ({ icon: Icon, label, value, sub, tone }) => {
  const tones = {
    red: { bg: "bg-gradient-to-br from-[#FEF7F6] to-[#FDECEA]", ring: "border-[#F4C7B9]", ic: "text-[#DC2626]" },
    amber: { bg: "bg-gradient-to-br from-[#FFF8E6] to-[#FCEDBF]", ring: "border-[#F9E4B7]", ic: "text-[#B45309]" },
    green: { bg: "bg-gradient-to-br from-[#EEF7EC] to-[#D4E0D1]", ring: "border-[#C6D4C2]", ic: "text-[#15803D]" },
    slate: { bg: "bg-white", ring: "border-[#E5E2D9]", ic: "text-[#5C5A55]" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`rounded-xl border ${t.ring} ${t.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white shadow-sm inline-flex items-center justify-center">
          <Icon size={20} weight="fill" className={t.ic}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-bold">{label}</div>
          <div className="font-heading text-2xl mt-0.5 text-[#111827]">{value}</div>
          {sub && <div className="text-[11px] text-[#5C5A55] mt-0.5 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
};

// ─── "Not-available-in-your-location" overlay editor ─────────────
const UnavailableOverlayEditor = ({ pageKey, value, onChange }) => {
  const enabled = !!value.enabled;
  return (
    <div className={`rounded-xl border overflow-hidden transition-shadow ${enabled ? "border-[#B45309] shadow-[0_4px_20px_rgba(180,83,9,0.15)]" : "border-[#E5E2D9]"}`}
          data-testid={`overlay-editor-${pageKey}`}>
      <div className={`px-4 py-3 flex items-center gap-3 ${enabled ? "bg-gradient-to-r from-[#FFF8E6] to-[#FEF3C7]" : "bg-[#FAFAF7]"}`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${enabled ? "bg-[#B45309] text-white" : "bg-white text-[#8A8780] border border-[#E5E2D9]"}`}>
          <EyeSlash size={16} weight={enabled ? "fill" : "regular"}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#5C5A55]">
            Availability overlay · <span className="font-mono">/{pageKey}</span>
          </div>
          <div className="text-[12px] text-[#5C5A55] mt-0.5">
            {enabled
              ? "Customers in this tenant see a glassy overlay saying the page is unavailable in their location."
              : "The page is fully accessible. Toggle ON to soft-lock this page for all customers of this tenant."}
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer"
                data-testid={`overlay-toggle-${pageKey}`}>
          <span className="text-[11px] uppercase tracking-wider font-bold text-[#5C5A55]">
            {enabled ? "ON" : "OFF"}
          </span>
          <span className={`relative inline-flex w-11 h-6 rounded-full transition ${enabled ? "bg-[#B45309]" : "bg-[#D6D3CB]"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition ${enabled ? "translate-x-5" : ""}`}/>
            <input type="checkbox" checked={enabled}
                    onChange={(e) => onChange({ enabled: e.target.checked })}
                    className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"/>
          </span>
        </label>
      </div>
      {enabled && (
        <div className="px-4 py-3 space-y-2.5 bg-white border-t border-[#F0EEE7]">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#5C5A55] mb-1">
              Title (headline)
            </label>
            <input
              type="text"
              value={value.title || ""}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="This feature is currently not available in your location"
              maxLength={140}
              data-testid={`overlay-title-${pageKey}`}
              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm outline-none focus:border-[#B45309] focus:ring-1 focus:ring-[#B45309]"/>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#5C5A55] mb-1">
              Sub-text (optional · one line)
            </label>
            <input
              type="text"
              value={value.subtitle || ""}
              onChange={(e) => onChange({ subtitle: e.target.value })}
              placeholder="We're working to bring Rescigo Health services to your area soon."
              maxLength={240}
              data-testid={`overlay-subtitle-${pageKey}`}
              className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm outline-none focus:border-[#B45309] focus:ring-1 focus:ring-[#B45309]"/>
          </div>
          <div className="text-[10px] text-[#8A8780] italic">
            Tip: page contents stay slightly visible behind a blur — customers know what&apos;s coming.
          </div>
        </div>
      )}
    </div>
  );
};
